/**
 * Pajangan lembar log untuk kurva taksiran, beberapa sumur berdampingan.
 *
 * `EstimateComparison` menjawab satu pertanyaan dengan baik — seberapa jauh
 * taksiran meleset di *satu* sumur — dan justru karena itu ia tidak bisa
 * menjawab dua pertanyaan yang orang tanyakan sesudahnya:
 *
 * **"Apakah sumur buta ini terlihat seperti sumur latihnya?"** Angka R² sudah
 * mengatakan bahwa ia lebih buruk; yang tidak dikatakan angka adalah *bentuk*
 * kegagalannya. Taksiran yang mendatar di sumur buta sementara di sumur latih
 * ia mengikuti tiap lekuk adalah model yang menghafal, dan itu hanya terlihat
 * ketika kedua sumur berdiri bersebelahan pada skala nilai yang sama.
 *
 * **"Lalu bagaimana bentuk taksiran di sumur yang memang tidak punya
 * kurvanya?"** Itu sumur yang menjadi alasan seluruh modul taksiran ada.
 * Ia dipajang dengan kolom terukur yang kosong dan tanpa metrik — sebuah
 * angka yang dikarang untuk kurva yang tidak ada lebih buruk daripada kolom
 * kosong yang jujur.
 *
 * Tiga keputusan tata letaknya:
 *
 * - **Satu skala nilai untuk seluruh sumur.** Skala per sumur akan membuat
 *   taksiran yang memampatkan rentangnya tampak sempurna, sebab kurva yang
 *   diregangkan sendiri-sendiri selalu berimpit.
 * - **Sumbu kedalaman boleh bersama atau masing-masing.** Bersama benar untuk
 *   sumur yang menembus selang yang sama; masing-masing benar untuk sumur yang
 *   tidak, dan memaksakan yang pertama pada keadaan kedua hanya menghasilkan
 *   dua kolom yang sebagian besarnya kosong.
 * - **Kursor membaca sampel terdekat, bukan hasil interpolasi.** Angka yang
 *   terbaca di layar harus angka yang benar-benar ada di berkasnya.
 */
import { useEffect, useMemo, useRef, useState } from "react";

import { api } from "../api";
import type { LogModel, LogTrace, LogTraces, Well } from "../types";
import { Button, Checkbox, Empty, Loading, Notice, Panel, Segmented } from "./ui";
import { count } from "../lib/plural";
import { zoomWindow as zoomDepth } from "../lib/plotzoom";

/** Tinggi kolom log dalam piksel. Setinggi lembar yang masih muat di layar. */
const TRACK_H = 420;

const ROLE_LABEL: Record<LogTrace["role"], string> = {
  trained: "trained",
  blind: "blind",
  unused: "outside training",
};

const ROLE_COLOR: Record<LogTrace["role"], string> = {
  // Buta adalah satu-satunya yang mengukur daya ramal, jadi ia yang diberi
  // warna paling terang; latih hanya mengatakan model itu bisa menghafal.
  blind: "var(--accent-bright)",
  trained: "var(--ink-4)",
  unused: "var(--amber)",
};

function num(value: number | null | undefined, digits = 2): string {
  return value === null || value === undefined || !Number.isFinite(value)
    ? "—"
    : value.toFixed(digits);
}

/** Rentang berhingga sebuah deret, atau `null` bila tak satu pun angka. */
export function span(values: (number | null)[]): [number, number] | null {
  let lo = Infinity;
  let hi = -Infinity;
  for (const v of values) {
    if (v === null || !Number.isFinite(v)) continue;
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  if (lo === Infinity) return null;
  return lo === hi ? [lo - 1, hi + 1] : [lo, hi];
}

/**
 * Skala nilai bersama untuk seluruh kurva yang dipajang.
 *
 * Terukur dan taksiran masuk ke dalam skala yang sama, dan seluruh sumur juga:
 * dua kolom yang masing-masing diregangkan ke rentangnya sendiri tidak bisa
 * dibandingkan dengan mata, padahal membandingkan dengan mata adalah satu-
 * satunya alasan mereka dipajang bersebelahan.
 */
export function sharedValueSpan(traces: LogTrace[]): [number, number] {
  let lo = Infinity;
  let hi = -Infinity;
  for (const trace of traces) {
    for (const deret of [trace.measured, trace.estimated]) {
      const s = deret ? span(deret) : null;
      if (!s) continue;
      lo = Math.min(lo, s[0]);
      hi = Math.max(hi, s[1]);
    }
  }
  if (lo === Infinity) return [0, 1];
  const pad = (hi - lo) * 0.04;
  return [lo - pad, hi + pad];
}

/**
 * Jendela kedalaman sesudah dizum, sebagai pecahan 0–1 dari rentang penuh.
 *
 * Zum berpusat pada kursor, bukan pada tengah kolom: yang diperbesar orang
 * adalah lapisan yang sedang ditunjuknya, dan zum yang melompat ke tengah
 * memaksa menggeser lagi sesudah tiap putaran roda.
 *
 * Hitungannya sendiri tinggal di `lib/plotzoom`, tempat sumbu crossplot LRLC
 * juga mengambilnya: "jendela sesudah dizum di sekitar kursor" adalah soal
 * yang sama persis entah yang membentang di sumbunya kedalaman atau
 * resistivitas, dan dua salinan yang dipelihara terpisah adalah dua gambar
 * yang cepat atau lambat akan berbeda pendapat tentang arah roda tetikus.
 * Yang tinggal di sini hanyalah ejaan kolom kedalaman — atas dan bawah — dan
 * lantainya sendiri: 2% lembar masih memuat beberapa sampel, sedangkan
 * seperseribunya tidak memuat satu pun.
 */
export function zoomWindow(
  view: { top: number; bottom: number },
  at: number,
  factor: number,
): { top: number; bottom: number } {
  const { lo, hi } = zoomDepth({ lo: view.top, hi: view.bottom }, at, factor, 0.02);
  return { top: lo, bottom: hi };
}

/** Indeks sampel terdekat ke kedalaman ini; −1 bila tak ada satu pun. */
export function nearestSample(md: (number | null)[], depth: number): number {
  let best = -1;
  let gap = Infinity;
  md.forEach((d, i) => {
    if (d === null || !Number.isFinite(d)) return;
    const jarak = Math.abs(d - depth);
    if (jarak < gap) {
      gap = jarak;
      best = i;
    }
  });
  return best;
}

export function EstimateWellLog({
  model,
  wells,
}: {
  model: LogModel;
  wells: Well[];
}) {
  const [picked, setPicked] = useState<number[]>([]);
  const [data, setData] = useState<LogTraces | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showMeasured, setShowMeasured] = useState(true);
  const [showEstimated, setShowEstimated] = useState(true);
  const [showDiff, setShowDiff] = useState(true);
  const [sameAxis, setSameAxis] = useState<"bersama" | "masing">("bersama");
  const [view, setView] = useState({ top: 0, bottom: 1 });
  const [cursor, setCursor] = useState<number | null>(null);
  const drag = useRef<{ y: number; top: number; bottom: number } | null>(null);
  const sheet = useRef<HTMLDivElement | null>(null);

  /** Peran tiap sumur, dari modelnya sendiri dan bukan disusun ulang di sini. */
  const roleOf = (name: string): LogTrace["role"] =>
    (model.wells_blind ?? []).includes(name)
      ? "blind"
      : (model.wells_trained ?? []).includes(name)
        ? "trained"
        : "unused";

  const toggle = (id: number) =>
    setPicked((now) => (now.includes(id) ? now.filter((x) => x !== id) : [...now, id]));

  const draw = async () => {
    if (!picked.length) return;
    setBusy(true);
    setError(null);
    try {
      const out = await api.post<LogTraces>(`/api/log-models/${model.id}/traces/`, {
        wells: picked,
      });
      setData(out);
      setView({ top: 0, bottom: 1 });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
      setData(null);
    } finally {
      setBusy(false);
    }
  };

  const traces = data?.wells ?? [];
  const [vLo, vHi] = useMemo(() => sharedValueSpan(traces), [traces]);
  const depthSpan = useMemo(() => {
    const all = traces.flatMap((t) => span(t.md) ?? []);
    return all.length ? [Math.min(...all), Math.max(...all)] : [0, 1];
  }, [traces]);

  /** Rentang kedalaman yang dipakai kolom ini sebelum dizum. */
  const baseRange = (trace: LogTrace): [number, number] => {
    if (sameAxis === "bersama") return [depthSpan[0], depthSpan[1]];
    return span(trace.md) ?? [depthSpan[0], depthSpan[1]];
  };

  /** Rentang yang benar-benar tergambar, sesudah jendela zum diterapkan. */
  const shownRange = (trace: LogTrace): [number, number] => {
    const [lo, hi] = baseRange(trace);
    return [lo + (hi - lo) * view.top, lo + (hi - lo) * view.bottom];
  };

  // Rodanya dipasang sendiri, bukan lewat `onWheel`. React memasang `wheel`
  // sebagai listener pasif di akar halaman, jadi `preventDefault()` di dalam
  // sebuah `onWheel` diabaikan: layarnya ikut bergulir sambil kolomnya dizum —
  // dan lembar log yang melompat ke bawah pada tiap putaran roda tidak bisa
  // dipakai — sementara Chromium mencatat "Unable to preventDefault inside
  // passive event listener invocation" sebagai error di konsol. Satu listener
  // non-pasif di baris kolomnya menutup keduanya, dan kolom mana yang
  // dimaksudnya dibaca dari sasaran peristiwanya.
  useEffect(() => {
    const row = sheet.current;
    if (!row) return;
    const onWheel = (event: WheelEvent) => {
      const track = (event.target as Element | null)?.closest?.("[data-testid^='track-']");
      const box = track?.getBoundingClientRect();
      if (!box || !box.height) return;
      event.preventDefault();
      const at = view.top + ((event.clientY - box.top) / box.height) * (view.bottom - view.top);
      setView(zoomWindow(view, at, event.deltaY > 0 ? 1.25 : 0.8));
    };
    row.addEventListener("wheel", onWheel, { passive: false });
    return () => row.removeEventListener("wheel", onWheel);
  }, [view]);

  const onPointerDown = (event: React.PointerEvent) => {
    (event.target as Element).setPointerCapture?.(event.pointerId);
    drag.current = { y: event.clientY, top: view.top, bottom: view.bottom };
  };

  const onPointerMove = (event: React.PointerEvent) => {
    const box = event.currentTarget.getBoundingClientRect();
    const f = (event.clientY - box.top) / box.height;
    if (drag.current) {
      const height = drag.current.bottom - drag.current.top;
      const shift = ((event.clientY - drag.current.y) / box.height) * height;
      const top = Math.max(0, Math.min(1 - height, drag.current.top - shift));
      setView({ top, bottom: top + height });
      return;
    }
    setCursor(Math.max(0, Math.min(1, f)));
  };

  const stopDrag = () => {
    drag.current = null;
  };

  const path = (trace: LogTrace, values: (number | null)[] | null): string => {
    if (!values) return "";
    const [dLo, dHi] = shownRange(trace);
    const parts: string[] = [];
    let open = false;
    values.forEach((v, i) => {
      const d = trace.md[i];
      if (v === null || d === null || !Number.isFinite(v) || !Number.isFinite(d)) {
        open = false;
        return;
      }
      const x = ((v - vLo) / (vHi - vLo || 1)) * 100;
      const y = ((d - dLo) / (dHi - dLo || 1)) * 100;
      parts.push(`${open ? "L" : "M"}${x.toFixed(2)},${y.toFixed(2)}`);
      open = true;
    });
    return parts.join(" ");
  };

  /** Selisih terukur−taksiran sebagai pita, hanya di kedalaman yang punya keduanya. */
  const diffBands = (trace: LogTrace): string[] => {
    if (!trace.measured) return [];
    const [dLo, dHi] = shownRange(trace);
    const bands: string[] = [];
    let atas: string[] = [];
    let bawah: string[] = [];
    const tutup = () => {
      if (atas.length > 1) bands.push(`M${atas.join(" L")} L${bawah.reverse().join(" L")} Z`);
      atas = [];
      bawah = [];
    };
    trace.md.forEach((d, i) => {
      const m = trace.measured![i];
      const e = trace.estimated[i];
      if (d === null || m === null || e === null) {
        tutup();
        return;
      }
      const y = ((d - dLo) / (dHi - dLo || 1)) * 100;
      atas.push(`${(((m - vLo) / (vHi - vLo || 1)) * 100).toFixed(2)},${y.toFixed(2)}`);
      bawah.push(`${(((e - vLo) / (vHi - vLo || 1)) * 100).toFixed(2)},${y.toFixed(2)}`);
    });
    tutup();
    return bands;
  };

  const readout = (trace: LogTrace) => {
    if (cursor === null) return null;
    const [dLo, dHi] = shownRange(trace);
    const depth = dLo + (dHi - dLo) * cursor;
    const i = nearestSample(trace.md, depth);
    if (i < 0) return null;
    const m = trace.measured?.[i] ?? null;
    const e = trace.estimated[i] ?? null;
    return {
      md: trace.md[i],
      measured: m,
      estimated: e,
      diff: m !== null && e !== null ? e - m : null,
    };
  };

  return (
    <Panel
      title="Estimated log sheet"
      meta={data ? `${count(traces.length, "well")} · ${data.target}` : `${model.target}`}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <span style={{ font: "400 10.5px/1.55 var(--font-sans)", color: "var(--ink-5)" }}>
          Pick the wells to display — training wells, blind wells, or wells with
          no {model.target} curve at all. The value scale is one for all of them,
          because columns stretched on their own always look alike.
        </span>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {/*
            Tidak ada yang disaring di sini. Sumur rencana biasanya memang
            belum punya kurva masukan, tetapi menyembunyikannya berarti
            menyembunyikan pula sebabnya: yang memilihnya tidak menemukan
            namanya dan tidak tahu apakah ia hilang karena statusnya, karena
            kurvanya, atau karena ia tidak ada di proyek ini sama sekali.
            Ditawarkan apa adanya, `traces` memulangkan alasannya per sumur —
            "tanpa kurva masukan ini dan itu pada satu sumbu kedalaman" — dan
            alasan itulah yang menjawab pertanyaannya.
          */}
          {wells.map((well) => {
            const role = roleOf(well.name);
            const on = picked.includes(well.id);
            return (
              <button
                key={well.id}
                type="button"
                onClick={() => toggle(well.id)}
                aria-pressed={on}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "3px 8px",
                  borderRadius: 5,
                  border: `1px solid ${on ? "var(--accent)" : "var(--line-strong)"}`,
                  background: on ? "var(--selected)" : "transparent",
                  color: "var(--ink-2)",
                  font: "500 10.5px var(--font-sans)",
                  cursor: "pointer",
                }}
              >
                {well.name}
                <span style={{ font: "400 9px var(--font-mono)", color: ROLE_COLOR[role] }}>
                  {ROLE_LABEL[role]}
                </span>
              </button>
            );
          })}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <Button variant="primary" busy={busy} disabled={!picked.length} onClick={draw}>
            {picked.length ? `Display ${count(picked.length, "well")}` : "Display wells"}
          </Button>
          <Checkbox checked={showMeasured} onChange={setShowMeasured} label="Measured" />
          <Checkbox checked={showEstimated} onChange={setShowEstimated} label="Estimated" />
          <Checkbox checked={showDiff} onChange={setShowDiff} label="Difference" />
          <span style={{ flex: 1 }} />
          <Segmented
            value={sameAxis}
            options={[
              { value: "bersama", label: "Shared axis" },
              { value: "masing", label: "Each on its own" },
            ]}
            onChange={(v) => setSameAxis(v as "bersama" | "masing")}
          />
          <Button
            variant="ghost"
            disabled={view.top === 0 && view.bottom === 1}
            onClick={() => setView({ top: 0, bottom: 1 })}
          >
            Fit
          </Button>
        </div>

        {error && <Notice tone="error">{error}</Notice>}
        {busy && !data && <Loading label="Estimating the curve…" />}
        {!busy && !data && !error && (
          <Empty>Nothing on display yet. Pick the wells, then press “Display”.</Empty>
        )}

        {data && data.skipped.length > 0 && (
          <Notice>
            {`${count(data.skipped.length, "well")} not displayed — `}
            {data.skipped.map((r) => `${r.well}: ${r.reason}`).join(" · ")}
          </Notice>
        )}

        {data && traces.length > 0 && (
          <>
            <div
              ref={sheet}
              style={{ display: "flex", gap: 10, alignItems: "stretch" }}
              onPointerLeave={() => {
                setCursor(null);
                stopDrag();
              }}
            >
              {traces.map((trace) => {
                const [dLo, dHi] = shownRange(trace);
                const baca = readout(trace);
                return (
                  <div key={trace.well_id} style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                      <span style={{ font: "600 11px var(--font-sans)", color: "var(--ink)" }}>
                        {trace.well}
                      </span>
                      <span
                        style={{ font: "500 9px var(--font-mono)", color: ROLE_COLOR[trace.role] }}
                      >
                        {ROLE_LABEL[trace.role]}
                      </span>
                      <span style={{ flex: 1 }} />
                      <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-6)" }}>
                        {trace.estimated_note
                          ? "no estimate"
                          : trace.metrics
                            ? `R² ${num(trace.metrics.r2, 3)}`
                            : "no measured curve"}
                      </span>
                    </div>
                    <div
                      data-testid={`track-${trace.well}`}
                      onPointerDown={onPointerDown}
                      onPointerMove={onPointerMove}
                      onPointerUp={stopDrag}
                      style={{
                        position: "relative",
                        height: TRACK_H,
                        marginTop: 4,
                        border: "1px solid var(--line-strong)",
                        borderRadius: 4,
                        background: "var(--canvas)",
                        cursor: drag.current ? "grabbing" : "crosshair",
                        touchAction: "none",
                        overflow: "hidden",
                      }}
                    >
                      <svg
                        viewBox="0 0 100 100"
                        preserveAspectRatio="none"
                        style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                        aria-label={`Estimated ${data.target} on ${trace.well}`}
                      >
                        {showDiff &&
                          diffBands(trace).map((d, i) => (
                            <path key={i} d={d} fill="var(--amber)" fillOpacity={0.16} stroke="none" />
                          ))}
                        {showMeasured && trace.measured && (
                          <path
                            d={path(trace, trace.measured)}
                            fill="none"
                            stroke="var(--ink-2)"
                            strokeWidth={1.1}
                            vectorEffect="non-scaling-stroke"
                          />
                        )}
                        {showEstimated && (
                          <path
                            d={path(trace, trace.estimated)}
                            fill="none"
                            stroke="var(--accent-bright)"
                            strokeWidth={1.2}
                            strokeDasharray={trace.measured ? "4 2" : undefined}
                            vectorEffect="non-scaling-stroke"
                          />
                        )}
                        {cursor !== null && (
                          <line
                            x1={0}
                            x2={100}
                            y1={cursor * 100}
                            y2={cursor * 100}
                            stroke="var(--accent)"
                            strokeWidth={1}
                            strokeOpacity={0.5}
                            vectorEffect="non-scaling-stroke"
                          />
                        )}
                      </svg>
                      {/* Kolom yang seluruhnya kosong mengatakan sebabnya di
                          tempat kurvanya seharusnya berdiri. Tanpa itu ia tidak
                          bisa dibedakan dari kegagalan menggambar — dan yang
                          sebenarnya terjadi bisa dibetulkan orangnya sendiri,
                          dengan memilih kurva masukan yang memang bertampalan
                          di sumur ini. */}
                      {trace.estimated_note && (
                        <span
                          style={{
                            position: "absolute",
                            inset: "0 12px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            font: "400 10px/1.6 var(--font-sans)",
                            color: "var(--ink-5)",
                            pointerEvents: "none",
                          }}
                        >
                          {trace.estimated_note}
                        </span>
                      )}
                      <span
                        style={{
                          position: "absolute",
                          left: 5,
                          top: 4,
                          font: "400 9px var(--font-mono)",
                          color: "var(--ink-6)",
                          pointerEvents: "none",
                        }}
                      >
                        {dLo.toFixed(0)} m
                      </span>
                      <span
                        style={{
                          position: "absolute",
                          left: 5,
                          bottom: 4,
                          font: "400 9px var(--font-mono)",
                          color: "var(--ink-6)",
                          pointerEvents: "none",
                        }}
                      >
                        {dHi.toFixed(0)} m
                      </span>
                    </div>
                    <div
                      style={{
                        marginTop: 4,
                        font: "400 9.5px var(--font-mono)",
                        color: "var(--ink-5)",
                        minHeight: 26,
                        lineHeight: 1.5,
                      }}
                    >
                      {baca ? (
                        <>
                          MD {num(baca.md, 1)} · terukur {num(baca.measured)} · taksiran{" "}
                          {num(baca.estimated)}
                          {baca.diff !== null && (
                            <>
                              {" · selisih "}
                              <span style={{ color: "var(--amber)" }}>{num(baca.diff)}</span>
                            </>
                          )}
                        </>
                      ) : (
                        trace.measured_note ?? "Point at the column to read its number."
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-6)" }}>
              {`${data.target} scale ${num(vLo)} – ${num(vHi)} · wheel: zoom · drag: pan`}
              {sameAxis === "bersama"
                ? " · shared depth axis"
                : " · each column on its own range"}
            </span>
          </>
        )}
      </div>
    </Panel>
  );
}
